/**
 * @file   QuickStatsStrip.js
 * @brief  Three inline stat pills for Match tab: Active Matches, Peak Demand, Most Demand range.
 *         Only renders for Tier 1-2 (3+ matches).
 */
import React, { useMemo } from "react";
import {
  formatCompactPrice,
  computeDemandBins,
  computeSweetSpotRange,
  computePeakBinIndex,
  sweetSpotLabelRange,
} from "../../../helpers/priceCapUtils";
import "./QuickStatsStrip/quick_stats_strip.scss";

const QuickStatsStrip = ({ matchCount, prices, adType, rangeMin, rangeMax }) => {
  // Peak demand price, computed with the SAME [rangeMin, rangeMax] binning as
  // DemandHistogram so the stat always matches the tallest bar on the chart.
  // Hooks must run unconditionally, so this stays above the early return below.
  const peakPrice = useMemo(() => {
    if (!prices || prices.length < 3) return null;
    const demand = computeDemandBins(prices, rangeMin, rangeMax);
    const peakIdx = computePeakBinIndex(demand);
    if (peakIdx == null) return null;
    const peak = demand.bins[peakIdx];
    return (peak.start + peak.end) / 2;
  }, [prices, rangeMin, rangeMax]);

  const sweetSpot = useMemo(
    () => computeSweetSpotRange(prices, adType, rangeMin, rangeMax),
    [prices, adType, rangeMin, rangeMax],
  );

  // Needs 3+ data points to be meaningful. Guard runs AFTER the hooks above so
  // the hook count stays constant across renders (React rules of hooks).
  if (!prices || prices.length < 3) return null;

  // Use server-side totalCount for Active Buyers (not prices.length which is capped by page size)
  const activeBuyerCount = matchCount || prices.length;
  const sweetLabel = sweetSpotLabelRange(sweetSpot);

  return (
    <div className="quick-stats-strip" data-tour="quick-stats">
      <div className="quick-stats-strip__pill">
        <span className="quick-stats-strip__value">{activeBuyerCount}</span>
        <span className="quick-stats-strip__label">Active Buyers</span>
        <span className="quick-stats-strip__sublabel">looking for properties like yours</span>
      </div>
      {peakPrice != null && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value">{formatCompactPrice(peakPrice)}</span>
          <span className="quick-stats-strip__label">Peak Demand</span>
          <span className="quick-stats-strip__sublabel">where most buyers cluster</span>
        </div>
      )}
      {sweetSpot && (
        <div className="quick-stats-strip__pill">
          <span className="quick-stats-strip__value quick-stats-strip__value--range">
            {formatCompactPrice(sweetLabel.min)} - {formatCompactPrice(sweetLabel.max)}
          </span>
          <span className="quick-stats-strip__label">Sweet Spot</span>
          <span className="quick-stats-strip__sublabel">highest concentration of demand</span>
        </div>
      )}
    </div>
  );
};

export default QuickStatsStrip;
